import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DocumentViewerUser {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface FileManagerLocksState {
  // Order Lock State (existing, moved from FileManagerV3Slice)
  orderLock: {
    isActive: boolean;
    isOwnLock: boolean;
    byUserId: number | null;
    byUserName: string | null;
    at: string | null;
    isRequesting: boolean;
  };

  // Annotation Lock State (per document)
  annotationLocksByDoc: Record<
    number,
    {
      isActive: boolean;
      isOwnLock: boolean;
      byUserId: number | null;
      byUserFirstName: string | null;
      byUserLastName: string | null;
      byUserEmail: string | null;
      lockedAt: string | null;
      expiresAt: string | null;
    }
  >;

  // Preview Tracking State (per document)
  documentViewers: Record<number, DocumentViewerUser[]>;
}

const initialState: FileManagerLocksState = {
  orderLock: {
    isActive: false,
    isOwnLock: false,
    byUserId: null,
    byUserName: null,
    at: null,
    isRequesting: false,
  },
  annotationLocksByDoc: {},
  documentViewers: {},
};

const fileManagerLocksSlice = createSlice({
  name: 'fileManagerLocks',
  initialState,
  reducers: {
    // Order Lock Reducers (migrated from FileManagerV3Slice)
    setOrderLock: (
      state,
      action: PayloadAction<{
        isActive: boolean;
        isOwnLock: boolean;
        byUserId: number | null;
        byUserName: string | null;
        at: string | null;
      }>
    ) => {
      state.orderLock = {
        ...state.orderLock,
        ...action.payload,
      };
    },
    clearOrderLock: (state) => {
      state.orderLock = {
        isActive: false,
        isOwnLock: false,
        byUserId: null,
        byUserName: null,
        at: null,
        isRequesting: false,
      };
    },
    setIsRequestingOrderLock: (state, action: PayloadAction<boolean>) => {
      state.orderLock.isRequesting = action.payload;
    },

    // Annotation Lock Reducers
    setAnnotationLock: (
      state,
      action: PayloadAction<{
        documentId: number;
        isActive: boolean;
        isOwnLock: boolean;
        byUserId: number | null;
        byUserFirstName: string | null;
        byUserLastName: string | null;
        byUserEmail: string | null;
        lockedAt: string | null;
        expiresAt: string | null;
      }>
    ) => {
      const { documentId, ...lockData } = action.payload;
      state.annotationLocksByDoc[documentId] = lockData;
    },
    clearAnnotationLock: (state, action: PayloadAction<number>) => {
      const documentId = action.payload;
      delete state.annotationLocksByDoc[documentId];
    },

    // Preview Tracking Reducers
    setDocumentViewers: (
      state,
      action: PayloadAction<{
        documentId: number;
        viewers: Array<{
          userId: number;
          firstName: string;
          lastName: string;
          email: string;
        }>;
      }>
    ) => {
      const { documentId, viewers } = action.payload;
      state.documentViewers[documentId] = viewers;
    },
    clearDocumentViewers: (state, action: PayloadAction<number>) => {
      const documentId = action.payload;
      delete state.documentViewers[documentId];
    },

    // Bulk Set Actions (for initial load from certification query)
    setAllAnnotationLocks: (
      state,
      action: PayloadAction<{
        locks: Array<{
          document_id: number;
          user_id: number;
          user: {
            id: number;
            first_name: string;
            last_name: string;
            email: string;
          };
          locked_at: string;
          expires_at: string;
        }>;
        currentUserId?: number;
      }>
    ) => {
      // Clear existing locks first
      state.annotationLocksByDoc = {};

      // Set each lock from payload
      action.payload.locks.forEach((lock) => {
        state.annotationLocksByDoc[lock.document_id] = {
          isActive: true,
          isOwnLock: action.payload.currentUserId
            ? lock.user_id === action.payload.currentUserId
            : false,
          byUserId: lock.user_id,
          byUserFirstName: lock.user.first_name,
          byUserLastName: lock.user.last_name,
          byUserEmail: lock.user.email,
          lockedAt: lock.locked_at,
          expiresAt: lock.expires_at,
        };
      });
    },
    setAllDocumentViewers: (
      state,
      action: PayloadAction<
        Record<
          number,
          Array<{
            user_id: number;
            first_name: string;
            last_name: string;
            email: string;
          }>
        >
      >
    ) => {
      // Clear existing viewers first
      state.documentViewers = {};

      // Set viewers for each document
      Object.entries(action.payload).forEach(([docId, viewers]) => {
        const documentId = parseInt(docId, 10);
        state.documentViewers[documentId] = viewers.map((v) => ({
          userId: v.user_id,
          firstName: v.first_name,
          lastName: v.last_name,
          email: v.email,
        }));
      });
    },

    // Reset action
    resetFileManagerLocks: () => initialState,
  },
});

export const {
  setOrderLock,
  clearOrderLock,
  setIsRequestingOrderLock,
  setAnnotationLock,
  clearAnnotationLock,
  setDocumentViewers,
  clearDocumentViewers,
  setAllAnnotationLocks,
  setAllDocumentViewers,
  resetFileManagerLocks,
} = fileManagerLocksSlice.actions;

export default fileManagerLocksSlice.reducer;
